import React, { useEffect, useState } from "react";
import { Gift, Loader2, X, CheckCircle2, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { useBranding, withAlpha } from "../lib/branding";

const STATUS_TINT = {
  pending: "bg-white/5 text-white/55 border-white/10",
  contacted: "bg-sky-500/10 text-sky-300 border-sky-500/30",
  signed: "bg-amber-500/10 text-amber-300 border-amber-500/30",
  rewarded: "bg-emerald-500/10 text-emerald-300 border-emerald-500/30",
  rejected: "bg-rose-500/10 text-rose-300 border-rose-500/30",
};

const STATUS_LABEL = {
  pending: "We'll reach out",
  contacted: "Patrick is talking",
  signed: "They signed",
  rewarded: "🎉 1 month free credited",
  rejected: "Closed",
};

export default function ReferralWidget() {
  const branding = useBranding();
  const accent = branding?.accent_color || "#FFB000";
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [rewardedCount, setRewardedCount] = useState(0);
  const [loading, setLoading] = useState(true);

  const refresh = async () => {
    try {
      const { data } = await api.get("/portal/referrals");
      setItems(data.items || []);
      setRewardedCount(data.rewarded_count || 0);
    } catch {
      // silent — widget should never block dashboard
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refresh(); }, []);

  return (
    <>
      <div
        className="rounded-2xl p-6 border relative overflow-hidden"
        style={{
          background: `linear-gradient(135deg, ${withAlpha(accent, 0.08)}, ${withAlpha(accent, 0.02)})`,
          borderColor: withAlpha(accent, 0.25),
        }}
        data-testid="portal-referral-widget"
      >
        <div className="absolute -top-12 -right-12 w-40 h-40 rounded-full blur-2xl pointer-events-none" style={{ background: withAlpha(accent, 0.15) }} />
        <div className="relative flex items-start gap-4">
          <div
            className="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 border"
            style={{ background: withAlpha(accent, 0.15), borderColor: withAlpha(accent, 0.3) }}
          >
            <Gift className="w-5 h-5" style={{ color: accent }} strokeWidth={1.75} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h3 className="text-base font-semibold">Refer a friend, get a month free</h3>
              {rewardedCount > 0 && (
                <span className="text-[10px] uppercase tracking-[0.1em] px-2 py-0.5 rounded-full border font-semibold flex items-center gap-1" style={{ background: withAlpha(accent, 0.15), borderColor: withAlpha(accent, 0.3), color: accent }}>
                  <Sparkles className="w-3 h-3" /> {rewardedCount} earned
                </span>
              )}
            </div>
            <p className="text-sm text-white/55 mt-1.5 leading-relaxed">
              Know a business that could use an AI front desk? Send them our way. When they sign up, you get <strong style={{ color: accent }}>1 month free</strong> on us.
            </p>

            <div className="flex items-center gap-3 mt-4 flex-wrap">
              <button
                onClick={() => setOpen(true)}
                data-testid="open-referral-form-btn"
                className="text-sm font-semibold px-4 py-2 rounded-lg text-black transition-all"
                style={{ background: accent, boxShadow: `0 0 14px ${withAlpha(accent, 0.25)}` }}
              >
                Refer someone →
              </button>
              {!loading && items.length > 0 && (
                <span className="text-[12px] text-white/45">{items.length} {items.length === 1 ? "referral" : "referrals"} sent</span>
              )}
            </div>

            {/* Compact recent referrals strip */}
            {items.length > 0 && (
              <div className="mt-5 pt-4 border-t border-white/5 space-y-2" data-testid="referral-history">
                {items.slice(0, 3).map((r) => (
                  <div key={r.id} className="flex items-center justify-between gap-3 text-xs">
                    <div className="min-w-0 flex-1">
                      <span className="text-white/85 font-medium truncate">{r.referee_company}</span>
                      <span className="text-white/40 ml-2">· {r.referee_contact_name}</span>
                    </div>
                    <span className={`text-[10px] uppercase tracking-[0.1em] px-2 py-0.5 rounded-full border font-medium whitespace-nowrap ${STATUS_TINT[r.status] || STATUS_TINT.pending}`}>
                      {STATUS_LABEL[r.status] || r.status}
                    </span>
                  </div>
                ))}
                {items.length > 3 && <div className="text-[11px] text-white/35 pt-1">+ {items.length - 3} more</div>}
              </div>
            )}
          </div>
        </div>
      </div>

      {open && <ReferralModal accent={accent} onClose={() => setOpen(false)} onSubmitted={refresh} />}
    </>
  );
}

function ReferralModal({ accent, onClose, onSubmitted }) {
  const [form, setForm] = useState({
    referee_company: "",
    referee_contact_name: "",
    referee_email: "",
    referee_phone: "",
    note: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.referee_company || !form.referee_contact_name || !form.referee_email) {
      toast.error("Company, contact name, and email are required");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/portal/referrals", form);
      setSubmitted(true);
      onSubmitted();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not submit referral");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm" onClick={onClose} data-testid="referral-modal">
      <div className="bg-[#121214] border border-white/10 rounded-2xl p-7 w-full max-w-md relative" onClick={(e) => e.stopPropagation()}>
        <button onClick={onClose} className="absolute top-4 right-4 text-white/50 hover:text-white" data-testid="close-referral-modal-btn">
          <X className="w-5 h-5" />
        </button>

        {submitted ? (
          <div className="text-center py-6" data-testid="referral-submitted">
            <div className="w-14 h-14 rounded-full mx-auto mb-4 flex items-center justify-center border" style={{ background: withAlpha(accent, 0.15), borderColor: withAlpha(accent, 0.4) }}>
              <CheckCircle2 className="w-7 h-7" style={{ color: accent }} strokeWidth={2} />
            </div>
            <h3 className="text-xl font-semibold mb-2">Thanks — we'll take it from here.</h3>
            <p className="text-sm text-white/55 mb-6">Patrick will reach out to <strong className="text-white/85">{form.referee_company}</strong> within 24 hours. We'll keep you posted as the deal moves.</p>
            <button
              onClick={onClose}
              className="text-sm font-semibold px-5 py-2.5 rounded-lg text-black"
              style={{ background: accent }}
            >
              Done
            </button>
          </div>
        ) : (
          <>
            <div className="flex items-center gap-3 mb-1">
              <Gift className="w-5 h-5" style={{ color: accent }} />
              <h3 className="text-xl font-semibold">Refer a business</h3>
            </div>
            <p className="text-sm text-white/55 mb-6">Quick referral. They sign up → you get a free month on your subscription.</p>

            <form onSubmit={submit} className="space-y-3" data-testid="referral-form">
              <Field label="Company name" required>
                <input
                  value={form.referee_company}
                  onChange={(e) => set("referee_company", e.target.value)}
                  data-testid="referral-company-input"
                  className="inp"
                  placeholder="Acme Plumbing"
                  autoFocus
                />
              </Field>
              <Field label="Contact name" required>
                <input
                  value={form.referee_contact_name}
                  onChange={(e) => set("referee_contact_name", e.target.value)}
                  data-testid="referral-contact-name-input"
                  className="inp"
                  placeholder="Sarah Acme"
                />
              </Field>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Email" required>
                  <input
                    type="email"
                    value={form.referee_email}
                    onChange={(e) => set("referee_email", e.target.value)}
                    data-testid="referral-email-input"
                    className="inp"
                    placeholder="sarah@..."
                  />
                </Field>
                <Field label="Phone">
                  <input
                    type="tel"
                    value={form.referee_phone}
                    onChange={(e) => set("referee_phone", e.target.value)}
                    data-testid="referral-phone-input"
                    className="inp"
                    placeholder="optional"
                  />
                </Field>
              </div>
              <Field label="Anything we should know?" hint="Optional — what's their pain? Best time to reach? You vouching for them?">
                <textarea
                  value={form.note}
                  onChange={(e) => set("note", e.target.value)}
                  rows={3}
                  data-testid="referral-note-input"
                  className="inp"
                  placeholder="They're slammed with calls and dropping leads at night..."
                />
              </Field>

              <button
                type="submit"
                disabled={submitting}
                data-testid="submit-referral-btn"
                className="w-full text-sm font-semibold px-4 py-2.5 rounded-lg text-black disabled:opacity-60 flex items-center justify-center gap-2 mt-2"
                style={{ background: accent, boxShadow: `0 0 14px ${withAlpha(accent, 0.25)}` }}
              >
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Gift className="w-4 h-4" />}
                {submitting ? "Sending…" : "Send referral"}
              </button>
            </form>
          </>
        )}

        <style>{`
          .inp { width: 100%; padding: 10px 12px; background: rgba(0,0,0,0.3); border: 1px solid rgba(255,255,255,0.1); border-radius: 8px; color: #fff; font-size: 13.5px; outline: none; transition: border-color .2s; }
          .inp:focus { border-color: ${accent}; }
          .inp::placeholder { color: rgba(255,255,255,0.3); }
        `}</style>
      </div>
    </div>
  );
}

function Field({ label, hint, required, children }) {
  return (
    <label className="block">
      <div className="text-[11px] font-medium text-white/65 mb-1.5">
        {label} {required && <span style={{ color: "#FFB000" }}>*</span>}
      </div>
      {children}
      {hint && <div className="text-[11px] text-white/35 mt-1">{hint}</div>}
    </label>
  );
}
